import { useEffect } from 'react'

const MERCURE_URL = import.meta.env.VITE_MERCURE_URL ?? 'http://localhost/.well-known/mercure'

export interface MercureEventData {
  event: string
  [key: string]: unknown
}

/**
 * Subscribe to Mercure topics for the lifetime of the component.
 * Anonymous subscriber (allowed by Caddy config in dev). Topics are scoped per user id.
 */
export function useMercureSubscription(
  topics: string[],
  onMessage: (data: MercureEventData) => void,
  enabled = true,
): void {
  useEffect(() => {
    if (!enabled || topics.length === 0) return

    // Base = page origin so a RELATIVE MERCURE_URL (same-origin prod build,
    // e.g. "/.well-known/mercure") resolves; an absolute one (dev) ignores it.
    const url = new URL(MERCURE_URL, window.location.origin)
    for (const t of topics) {
      url.searchParams.append('topic', t)
    }

    const es = new EventSource(url.toString(), { withCredentials: false })

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as MercureEventData
        onMessage(data)
      } catch (e) {
        console.warn('Failed to parse Mercure event', e, event.data)
      }
    }

    es.onerror = (e) => {
      // EventSource auto-reconnects. Log once for visibility.
      console.warn('Mercure connection issue', e)
    }

    return () => {
      es.close()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, topics.join(','), onMessage])
}
